/**
 * InventoryDetailsModal — shared "Tech Data" dialog.
 * Shows images, documents, and line details for an inventory record.
 * Non-STANDARD users see a pending-approval gate instead of the data.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../Services/services';
import { getFiles } from '../api/files';
import { getInventory } from '../api/products';
import { getMarketplaceSources } from '../api/inventory';
import { ROLES, hasRole } from '../utils/roles';
import { resolveTechDataFields } from './marketplace/techDataFields';

// ── Helpers ───────────────────────────────────────────────────────────────────
const IMG_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']);

export function isImageFile(f) {
  const ext = (f.FileExtension || f.fileExtension || f.extension || '').toLowerCase().replace('.', '');
  const type = (f.FileType || f.fileType || f.ContentType || f.contentType || '').toLowerCase();
  return IMG_EXTS.has(ext) || type.startsWith('image/');
}

// A "part image" is the Product2 attachment stamped with the 'Part Image' File
// Type by core (jpg/png added via the Add Part Image action) — used to show the
// real part image instead of an unrelated attachment.
export function isPartImage(f) {
  const typeName = (f.FileTypeName || f.fileTypeName || '').trim().toLowerCase();
  return typeName === 'part image' && isImageFile(f);
}

export function isPdfFile(f) {
  const ext = (f.FileExtension || f.fileExtension || '').toLowerCase();
  const type = (f.FileType || f.fileType || f.ContentType || f.contentType || '').toLowerCase();
  return ext === 'pdf' || type === 'application/pdf';
}

export function fileTitle(f) {
  return f.Title || f.title || f.FileName || f.fileName || f.Name || f.name || 'Untitled';
}

export function fileVersionId(f) {
  return f.Id || f.id || f.ContentVersionId || f.contentVersionId;
}

// Removes duplicate files by their version id, preserving order (first wins).
export function dedupeFiles(list) {
  const seen = new Set();
  return (list || []).filter((f) => {
    const id = fileVersionId(f);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

const EXT_MIME = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', bmp: 'image/bmp', svg: 'image/svg+xml', pdf: 'application/pdf',
};

function extToMime(ext) {
  return EXT_MIME[(ext || '').toLowerCase().replace('.', '')] || 'application/octet-stream';
}

function base64ToBlob(base64, mime) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// The download endpoint (AvsConDownloadApi) returns JSON with a base64 `body`, not
// raw bytes — decode it here into a Blob URL usable as an <img>/document source.
export async function loadBlobUrl(contentVersionId) {
  const resp = await apiClient.get(`/v1/download/${encodeURIComponent(contentVersionId)}`);
  const data = resp?.data || {};
  const base64 = data.body || data.Body;
  if (!base64) return null;
  const mime = extToMime(data.FileExtension || data.fileExtension);
  return URL.createObjectURL(base64ToBlob(base64, mime));
}

export function fmtPrice(v) {
  if (v == null) return '—';
  const n = Number(v);
  return isNaN(n) ? '—' : `$${n.toFixed(2)}`;
}

// A tech-data value is "empty" when null/undefined or an empty string. Numbers
// (including 0) and booleans (including false) are meaningful and kept.
function isEmptyVal(v) {
  return v == null || v === '';
}

// Formats a tech-data value for display. Booleans render as Yes/No.
function fmtDetailVal(v) {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

export function CartIcon({ size = 13 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }} aria-hidden="true">
      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
    </svg>
  );
}

export function ImgPlaceholderSvg({ size = 28 }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" width={size} height={size}
      stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <path d="m21 15-5-5L5 21"/>
    </svg>
  );
}

export function ConditionBadge({ condition }) {
  if (!condition) return null;
  const MAP = {
    NE:  { bg: '#dcfce7', color: '#166534', label: 'New' },
    NS:  { bg: '#dcfce7', color: '#166534', label: 'New Surplus' },
    SV:  { bg: '#dbeafe', color: '#1e40af', label: 'Serviceable' },
    AR:  { bg: '#fef3c7', color: '#92400e', label: 'As Removed' },
    OH:  { bg: '#ede9fe', color: '#5b21b6', label: 'Overhauled' },
    RP:  { bg: '#fce7f3', color: '#9d174d', label: 'Repaired' },
    RFE: { bg: '#fce7f3', color: '#9d174d', label: 'Repairable' },
    IN:  { bg: '#ffedd5', color: '#9a3412', label: 'Inspected' },
    FN:  { bg: '#ecfdf5', color: '#065f46', label: 'Factory New' },
  };
  const m = MAP[condition?.toUpperCase()];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 700,
      backgroundColor: m?.bg || '#f3f4f6', color: m?.color || '#374151',
    }}>
      {m?.label || condition}
    </span>
  );
}

// ── Pending gate card ─────────────────────────────────────────────────────────
function PendingGate({ primaryColor, onClose }) {
  const primary = primaryColor || '#0066cc';
  return (
    <div style={m.overlay} role="dialog" aria-modal="true">
      <div style={{ ...m.panel, maxWidth: 480 }}>
        <div style={m.header}>
          <span style={m.headingPart}>Technical Data</span>
          <button style={m.closeX} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '48px 40px', textAlign: 'center' }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%',
            backgroundColor: `${primary}18`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <svg viewBox="0 0 24 24" fill="none" width={28} height={28}
              stroke={primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </div>
          <h3 style={{ margin: '0 0 12px', fontSize: 19, fontWeight: 700, color: '#111827' }}>
            Technical Data Will Appear Here
          </h3>
          <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.65, color: '#6b7280' }}>
            Inventory images, technical documentation, and traceability data are only visible to approved accounts. Once your account is approved, this dialog will show the full technical record.
          </p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            backgroundColor: `${primary}12`, border: `1px solid ${primary}30`,
            borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, color: primary,
          }}>
            <svg viewBox="0 0 24 24" fill="none" width={14} height={14}
              stroke={primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Approval usually happens within one business day
          </div>
        </div>
        <div style={{ ...m.footer, justifyContent: 'flex-end' }}>
          <button style={m.closeBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function InventoryDetailsModal({
  line,
  partNumber,        // string — part number for the header
  productId,         // Salesforce Product2 Id — used to pull the part image
  onClose,
  onAddToCart,       // async fn(line) — null if cart action not applicable
  primaryColor,
  cartBusy,
  userRole,          // if not STANDARD+, show pending gate
}) {
  const primary = primaryColor || '#0066cc';

  // Show gate for non-approved users
  if (!hasRole(userRole, ROLES.STANDARD)) {
    return <PendingGate primaryColor={primary} onClose={onClose} />;
  }

  return (
    <ModalContent
      line={line}
      partNumber={partNumber}
      productId={productId}
      onClose={onClose}
      onAddToCart={onAddToCart}
      primaryColor={primary}
      cartBusy={cartBusy}
    />
  );
}

function ModalContent({ line, partNumber, productId, onClose, onAddToCart, primaryColor, cartBusy }) {
  const inventoryId = line?.Id || line?.id || line?.sf_inventory_line_id;
  const partProductId = productId || line?.ProductId || line?.sf_product_id || line?.inscor__Product__c;
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [productFiles, setProductFiles] = useState([]);
  const [productFilesLoading, setProductFilesLoading] = useState(true);
  const [techDataFields, setTechDataFields] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});
  const [activeImg, setActiveImg] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const blobUrlsRef = useRef({});

  // Files attached to the specific inventory line (unit paperwork + photos).
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!inventoryId) { setFilesLoading(false); return; }
      try {
        const [fileData, sourcesData] = await Promise.allSettled([
          getFiles(inventoryId),
          getMarketplaceSources(),
        ]);
        if (cancelled) return;
        if (fileData.status === 'fulfilled') {
          const data = fileData.value;
          const list = Array.isArray(data) ? data : (data?.files || data?.records || []);
          setFiles(list);
        }
        if (sourcesData.status === 'fulfilled') {
          setTechDataFields(resolveTechDataFields(sourcesData.value?.techDataFields));
        } else {
          setTechDataFields(resolveTechDataFields(null));
        }
      } catch {
        if (!cancelled) {
          setFiles([]);
          setTechDataFields(resolveTechDataFields(null));
        }
      } finally {
        if (!cancelled) setFilesLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [inventoryId]);

  // Part image lives on the Product2 record. Pull its files so the thumbnail can
  // show the canonical part image even when the unit itself has no photos.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!partProductId) { setProductFilesLoading(false); return; }
      try {
        const data = await getFiles(partProductId);
        const list = Array.isArray(data) ? data : (data?.files || data?.records || []);
        if (!cancelled) setProductFiles(list);
      } catch {
        if (!cancelled) setProductFiles([]);
      } finally {
        if (!cancelled) setProductFilesLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [partProductId]);

  // Fetch the full Inventory Line "tech data" field set (grouped by section below).
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!inventoryId) { setDetailLoading(false); return; }
      try {
        const d = await getInventory(inventoryId);
        if (!cancelled) setDetail(d && typeof d === 'object' ? d : null);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [inventoryId]);

  const ensureBlobUrl = useCallback(async (id) => {
    if (blobUrlsRef.current[id]) return blobUrlsRef.current[id];
    try {
      const url = await loadBlobUrl(id);
      blobUrlsRef.current[id] = url;
      setBlobUrls((prev) => ({ ...prev, [id]: url }));
      return url;
    } catch { return null; }
  }, []);

  useEffect(() => {
    if (activeImg && !blobUrls[activeImg]) ensureBlobUrl(activeImg);
  }, [activeImg, blobUrls, ensureBlobUrl]);

  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => { Object.values(urls).forEach((u) => URL.revokeObjectURL(u)); };
  }, []);

  const handleViewFile = async (f) => {
    const id = fileVersionId(f);
    const url = await ensureBlobUrl(id);
    if (url) window.open(url, '_blank');
  };

  // Thumbnail shows only the Product's designated "Part Image" (File Type stamped
  // by core). If the part has no part image, we fall back to the placeholder rather
  // than showing an unrelated attachment. Documents/paperwork stay scoped to the line.
  const imgs = dedupeFiles(productFiles.filter(isPartImage));
  const docs = files.filter((f) => !isImageFile(f));

  // Once images are known, default the main viewer to the first (part) image.
  const firstImgId = imgs.length ? fileVersionId(imgs[0]) : null;
  useEffect(() => {
    if (!activeImg && firstImgId) setActiveImg(firstImgId);
  }, [firstImgId, activeImg]);

  const data = { ...(line || {}), ...(detail || {}) };
  const conditionLabel = data.Condition || data.inscor__Condition__c || data.condition || '';
  const lineKey = data.Id || data.id || '';
  const busy = cartBusy?.[lineKey];
  const allowed = new Set(techDataFields || []);
  const ALWAYS_VISIBLE = new Set(['PartNumber', 'Description', 'Condition']);
  const KEY_ALIASES = { Trace: 'TraceNumber', TagReference: 'TagNumber', InspectionNotes: 'Notes' };
  const isRowAllowed = (key) => {
    if (!key || ALWAYS_VISIBLE.has(key)) return true;
    if (allowed.has(key)) return true;
    const alias = KEY_ALIASES[key];
    return alias ? allowed.has(alias) : false;
  };

  // Merge the live line (basic fields) with the fetched tech-data detail (full
  // field set). Detail values win once loaded; line values render immediately.
  // Admin-selected Tech Data keys filter the body; identity fields always show.
  const sections = [
    {
      heading: 'Inventory',
      rows: [
        { key: 'PartNumber',   label: 'Part Number',    val: data.PartNumber || partNumber || data.ProductName },
        { key: 'Description',  label: 'Description',    val: data.Description },
        { key: 'Condition',    label: 'Condition Code', val: data.Condition || conditionLabel },
        { key: 'SerialNumber', label: 'Serial Number',  val: data.SerialNumber },
        { key: 'Serialized',   label: 'Serialized?',    val: data.Serialized },
        { key: 'OwnerCode',    label: 'Owner Code',     val: data.OwnerCode },
        { key: 'UOM',          label: 'UOM',            val: data.UOM },
        { key: 'KitVersion',   label: 'Kit Version',    val: data.KitVersion },
        { key: 'Manifest',     label: 'Manifest',       val: data.Manifest },
        { key: 'KitLot',       label: 'Kit Lot #',      val: data.KitLot },
      ],
    },
    {
      heading: 'Tag & Trace',
      rows: [
        { key: 'Source',               label: 'Source',                val: data.Source },
        { key: 'Source2',              label: 'Source 2',              val: data.Source2 },
        { key: 'Trace',                label: 'Trace',                 val: data.Trace || data.TraceNumber },
        { key: 'TraceType',            label: 'Trace Type',            val: data.TraceType },
        { key: 'ESN',                  label: 'ESN',                   val: data.ESN },
        { key: 'CountryOfOrigin',      label: 'Country of Origin',     val: data.CountryOfOrigin },
        { key: 'MfrCode',              label: 'MFR Code',              val: data.MfrCode },
        { key: 'MfrName',              label: 'MFR Name',              val: data.MfrName },
        { key: 'ManufactureDate',      label: 'Manufacture Date',      val: data.ManufactureDate },
        { key: 'OemPackaging',         label: 'OEM Packaging',         val: data.OemPackaging },
        { key: 'TagAgency',            label: 'Tag Agency',            val: data.TagAgency },
        { key: 'Tags',                 label: 'Tags',                  val: data.Tags },
        { key: 'TagReference',         label: 'Tag Reference',         val: data.TagReference || data.TagNumber },
        { key: 'TagDate',              label: 'Tag Date',              val: data.TagDate },
        { key: 'RepairType',           label: 'Repair Type',           val: data.RepairType },
        { key: 'TagAgency2',           label: 'Tag Agency 2',          val: data.TagAgency2 },
        { key: 'TagDate2',             label: 'Tag Date 2',            val: data.TagDate2 },
        { key: 'ConversionTagAgency',  label: 'Conversion Tag Agency', val: data.ConversionTagAgency },
        { key: 'ConversionTrace',      label: 'Conversion Trace',      val: data.ConversionTrace },
        { key: 'ConversionSource',     label: 'Conversion Source',     val: data.ConversionSource },
      ],
    },
    {
      heading: 'Weight & Dimension Information',
      rows: [
        { key: 'WeightUnits',    label: 'Weight Units',    val: data.WeightUnits },
        { key: 'GrossWeight',    label: 'Gross Weight',    val: data.GrossWeight },
        { key: 'NetWeight',      label: 'Net Weight',      val: data.NetWeight },
        { key: 'DimensionUnits', label: 'Dimension Units', val: data.DimensionUnits },
        { key: 'Length',         label: 'Length',          val: data.Length },
        { key: 'Width',          label: 'Width',           val: data.Width },
        { key: 'Height',         label: 'Height',          val: data.Height },
        { key: 'Oversized',      label: 'Oversized',       val: data.Oversized },
        { key: 'Volume',         label: 'Volume',          val: data.Volume },
      ],
    },
    {
      heading: 'Inspection Information',
      rows: [
        { key: 'ExpirationDate',      label: 'Expiration Date',       val: data.ExpirationDate },
        { key: 'LastInspectionDate',  label: 'Last Inspection Date',  val: data.LastInspectionDate },
        { key: 'NextInspectionDate',  label: 'Next Inspection Date',  val: data.NextInspectionDate },
        { key: 'LastStockCheckDate',  label: 'Last Stock Check Date', val: data.LastStockCheckDate },
        { key: 'BoxSize',             label: 'Box Size',              val: data.BoxSize },
        { key: 'VisualCheck',         label: 'Visual Check',          val: data.VisualCheck },
        { key: 'InspectedDate',       label: 'Inspected Date',        val: data.InspectedDate },
        { key: 'InspectedBy',         label: 'Inspected By',          val: data.InspectedBy },
        { key: 'InspectionNotes',     label: 'Inspection Notes',      val: data.InspectionNotes || data.Notes },
        { key: 'IncidentClearance',   label: 'Incident Clearance',    val: data.IncidentClearance },
        { key: 'PublicUseAircraft',   label: 'Public Use Aircraft',   val: data.PublicUseAircraft },
      ],
    },
  ]
    .map((s) => ({
      ...s,
      rows: s.rows.filter((r) => !isEmptyVal(r.val) && isRowAllowed(r.key)),
    }))
    .filter((s) => s.rows.length > 0);

  return (
    <div style={m.overlay} role="dialog" aria-modal="true">
      <div style={m.panel}>
        <div style={m.header}>
          <div>
            <span style={m.headingPart}>{partNumber}</span>
            {conditionLabel && <span style={{ marginLeft: 10 }}><ConditionBadge condition={conditionLabel} /></span>}
          </div>
          <button style={m.closeX} onClick={onClose}>✕</button>
        </div>

        <div style={m.body}>
          {/* Left — images + docs */}
          <div style={m.imgCol}>
            <div style={m.imgMain}>
              {activeImg && blobUrls[activeImg] ? (
                <img src={blobUrls[activeImg]} alt="Part" style={m.imgMainEl} />
              ) : activeImg ? (
                <div style={m.imgLoading}><div style={m.imgSpinner} /></div>
              ) : (
                <div style={m.imgEmpty}>
                  <ImgPlaceholderSvg size={48} />
                  <span style={{ marginTop: 10, fontSize: 13, color: '#9ca3af' }}>
                    {productFilesLoading ? 'Loading…' : 'No part image'}
                  </span>
                </div>
              )}
            </div>

            {imgs.length > 1 && (
              <div style={m.thumbRow}>
                {imgs.map((f) => {
                  const id = fileVersionId(f);
                  const url = blobUrls[id];
                  const active = id === activeImg;
                  return (
                    <button key={id}
                      style={{ ...m.thumb, ...(active ? m.thumbActive : {}) }}
                      onClick={() => { setActiveImg(id); if (!url) ensureBlobUrl(id); }}>
                      {url ? <img src={url} alt="" style={m.thumbImg} /> : <ImgPlaceholderSvg size={18} />}
                    </button>
                  );
                })}
              </div>
            )}

            {docs.length > 0 && (
              <div style={m.docsSection}>
                <div style={m.docsTitle}>Documents & Paperwork</div>
                {docs.map((f) => {
                  const id = fileVersionId(f);
                  const ext = (f.FileExtension || f.fileExtension || '').toUpperCase();
                  return (
                    <div key={id} style={m.docRow}>
                      <span style={m.docIcon}>{isPdfFile(f) ? '📄' : '📎'}</span>
                      <span style={m.docName}>{fileTitle(f)}</span>
                      {ext && <span style={m.docExt}>{ext}</span>}
                      <button style={{ ...m.docBtn, color: primaryColor, borderColor: `${primaryColor}40` }}
                        onClick={() => handleViewFile(f)}>View</button>
                    </div>
                  );
                })}
              </div>
            )}

            {!filesLoading && !productFilesLoading && files.length === 0 && productFiles.length === 0 && (
              <div style={m.noFiles}>No files attached to this record.</div>
            )}
          </div>

          {/* Right — details grouped by Inventory Line layout sections */}
          <div style={m.detailCol}>
            {sections.map((s) => (
              <div key={s.heading} style={m.detailSection}>
                <div style={m.detailTitle}>{s.heading}</div>
                <div>
                  {s.rows.map((r) => (
                    <div key={r.label} style={m.detailRow}>
                      <span style={m.detailLabel}>{r.label}</span>
                      <span style={m.detailVal}>{fmtDetailVal(r.val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {sections.length === 0 && (
              <p style={{ color: '#9ca3af', fontSize: 13 }}>
                {detailLoading ? 'Loading tech data…' : 'No additional details available.'}
              </p>
            )}
          </div>
        </div>

        <div style={m.footer}>
          {onAddToCart && (
            <button
              style={{ ...m.cartBtn, backgroundColor: primaryColor, opacity: busy ? 0.6 : 1 }}
              disabled={busy}
              onClick={() => onAddToCart(line)}
            >
              {busy ? 'Adding…' : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  + <CartIcon size={14} /> Add to Cart
                </span>
              )}
            </button>
          )}
          <button style={m.closeBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const m = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
  },
  panel: {
    backgroundColor: '#ffffff', borderRadius: 16,
    width: '100%', maxWidth: 900, maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.25)', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 24px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
  },
  headingPart: { fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px' },
  closeX: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280', padding: '4px 8px', borderRadius: 6 },
  body: { display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 },
  imgCol: {
    width: 280, flexShrink: 0, borderRight: '1px solid #e5e7eb',
    display: 'flex', flexDirection: 'column', padding: 16, gap: 12, overflowY: 'auto',
  },
  imgMain: {
    width: '100%', aspectRatio: '1', borderRadius: 12,
    backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  },
  imgMainEl: { width: '100%', height: '100%', objectFit: 'contain' },
  imgLoading: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  imgSpinner: {
    width: 28, height: 28, border: '3px solid #e5e7eb', borderTopColor: '#6b7280',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  },
  imgEmpty: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  thumbRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  thumb: {
    width: 52, height: 52, borderRadius: 8, border: '2px solid transparent', padding: 0,
    backgroundColor: '#f3f4f6', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  },
  thumbActive: { borderColor: '#6b7280' },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  docsSection: { borderTop: '1px solid #e5e7eb', paddingTop: 12 },
  docsTitle: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', marginBottom: 8 },
  docRow: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 12 },
  docIcon: { flexShrink: 0, fontSize: 14 },
  docName: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' },
  docExt: { fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, backgroundColor: '#f3f4f6', color: '#6b7280', flexShrink: 0 },
  docBtn: { flexShrink: 0, background: 'none', borderRadius: 5, border: '1px solid', padding: '2px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  noFiles: { fontSize: 12, color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', paddingTop: 8 },
  detailCol: { flex: 1, overflowY: 'auto', padding: '16px 20px' },
  detailSection: { marginBottom: 20 },
  detailTitle: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', marginBottom: 10 },
  detailRow: { display: 'flex', alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid #f3f4f6', gap: 10 },
  detailLabel: { width: 160, flexShrink: 0, color: '#6b7280', fontWeight: 600, fontSize: 12 },
  detailVal: { flex: 1, fontSize: 13, color: '#111827', fontWeight: 500 },
  footer: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '14px 24px',
    borderTop: '1px solid #e5e7eb', flexShrink: 0, backgroundColor: '#fafafa',
  },
  cartBtn: { color: 'white', border: 'none', borderRadius: 8, padding: '9px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  closeBtn: { backgroundColor: '#ffffff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, padding: '9px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
